import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, CheckCircle, Globe2, AlertTriangle,
  Loader2, Filter, X, Calendar, Users, Award, ChevronUp, ChevronDown, Search,
  ArrowUpRight, ArrowDownRight, BarChart3, AlertCircle, RefreshCw,
  Download
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { gtmDashboardAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveFilters, loadFilters, clearPageFilters } from '../utils/filterPersistence';
import { GTMApprovalInsights } from '../components/DashboardApprovalInsights';

// --- Filter Presets ---
const TIME_PRESETS = [
  { label: 'All Time', key: 'all' },
  { label: 'Last 7 days', key: '7d' },
  { label: 'This Week', key: 'this_week' },
  { label: 'This Month', key: 'this_month' },
  { label: 'Custom', key: 'custom' },
];

const COMPARE_PRESETS = [
  { label: 'No Comparison', key: 'none' },
  { label: 'Previous Week', key: 'prev_week' },
  { label: 'Previous Month', key: 'prev_month' },
  { label: 'Custom Range', key: 'custom' },
];

function getDateRange(preset) {
  const now = new Date();
  const toISO = (d) => d.toISOString().split('T')[0];
  switch (preset) {
    case '7d': {
      const from = new Date(now);
      from.setDate(now.getDate() - 7);
      return { date_from: toISO(from), date_to: toISO(now) };
    }
    case 'this_week': {
      const day = now.getDay();
      const from = new Date(now);
      from.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      return { date_from: toISO(from), date_to: toISO(now) };
    }
    case 'this_month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { date_from: toISO(from), date_to: toISO(now) };
    }
    default:
      return {};
  }
}

function getCompareRange(preset) {
  const now = new Date();
  const toISO = (d) => d.toISOString().split('T')[0];
  switch (preset) {
    case 'prev_week': {
      const day = now.getDay();
      const thisWeekStart = new Date(now);
      thisWeekStart.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      const prevEnd = new Date(thisWeekStart);
      prevEnd.setDate(prevEnd.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevEnd.getDate() - 6);
      return { date_from: toISO(prevStart), date_to: toISO(prevEnd) };
    }
    case 'prev_month': {
      const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { date_from: toISO(prevMonthStart), date_to: toISO(prevMonthEnd) };
    }
    default:
      return null;
  }
}

// --- Sub-components ---
const DeltaBadge = ({ delta }) => {
  if (!delta) return null;
  const isPositive = delta.diff >= 0;
  const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
      data-testid="delta-badge">
      <Icon size={12} />
      {delta.pct_change > 0 ? '+' : ''}{delta.pct_change}%
    </span>
  );
};

const GTMDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [usersList, setUsersList] = useState([]);

  // Pagination for Top Performers
  const [perfPage, setPerfPage] = useState(1);
  const [perfPageSize, setPerfPageSize] = useState(25);
  const [perfSearch, setPerfSearch] = useState('');
  // Pagination for At Risk
  const [riskPage, setRiskPage] = useState(1);
  const [riskPageSize, setRiskPageSize] = useState(25);
  const [riskSearch, setRiskSearch] = useState('');

  // Filter state - restore from localStorage
  const _sf = loadFilters(user?.id, 'gtm_dashboard');
  const [timePreset, setTimePreset] = useState(_sf?.timePreset || 'all');
  const [userFilter, setUserFilter] = useState(_sf?.userFilter || '');
  const [customFrom, setCustomFrom] = useState(_sf?.customFrom || '');
  const [customTo, setCustomTo] = useState(_sf?.customTo || '');
  const [comparePreset, setComparePreset] = useState('none');
  const [compCustomFrom, setCompCustomFrom] = useState('');
  const [compCustomTo, setCompCustomTo] = useState('');

  // Persist filters on change
  useEffect(() => {
    saveFilters(user?.id, 'gtm_dashboard', { timePreset, userFilter, customFrom, customTo });
  }, [timePreset, userFilter, customFrom, customTo, user?.id]);

  // Load users for filter
  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getUsers();
        const u = res.data?.users || res.data || [];
        setUsersList(Array.isArray(u) ? u : []);
      } catch { }
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (userFilter) filters.user = userFilter;
      if (timePreset === 'custom') {
        if (customFrom) filters.date_from = customFrom;
        if (customTo) filters.date_to = customTo;
      } else if (timePreset !== 'all') {
        const range = getDateRange(timePreset);
        if (range.date_from) filters.date_from = range.date_from;
        if (range.date_to) filters.date_to = range.date_to;
      }

      const payload = { filters };

      if (comparePreset !== 'none') {
        if (comparePreset === 'custom') {
          if (compCustomFrom || compCustomTo) {
            payload.comparison = {};
            if (compCustomFrom) payload.comparison.date_from = compCustomFrom;
            if (compCustomTo) payload.comparison.date_to = compCustomTo;
          }
        } else {
          const compRange = getCompareRange(comparePreset);
          if (compRange) payload.comparison = compRange;
        }
      }

      const res = await gtmDashboardAPI.getData(payload);
      setData(res.data);
    } catch (err) { console.error('GTM Dashboard error:', err); }
    finally { setLoading(false); }
  }, [timePreset, userFilter, customFrom, customTo, comparePreset, compCustomFrom, compCustomTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const primary = data?.primary;
  const comparison = data?.comparison;
  const deltas = data?.comparison_delta;

  const filteredPerformers = useMemo(() => {
    if (!primary?.top_performers) return [];
    if (!perfSearch.trim()) return primary.top_performers;
    const q = perfSearch.toLowerCase();
    return primary.top_performers.filter(p => p.user?.toLowerCase().includes(q));
  }, [primary?.top_performers, perfSearch]);

  const filteredAtRisk = useMemo(() => {
    if (!primary?.at_risk) return [];
    if (!riskSearch.trim()) return primary.at_risk;
    const q = riskSearch.toLowerCase();
    return primary.at_risk.filter(r => r.hotel_name?.toLowerCase().includes(q) || r.owner?.toLowerCase().includes(q));
  }, [primary?.at_risk, riskSearch]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-teal-500 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading GTM Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!primary) return <div className="text-center py-16 text-gray-400">Failed to load dashboard data</div>;

  return (
    <div className="space-y-5" data-testid="gtm-dashboard">
      {/* Approval & Verification Insights - TOP */}
      <GTMApprovalInsights />

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Time presets */}
            <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg p-1">
              {TIME_PRESETS.map(p => (
                <button key={p.key} onClick={() => setTimePreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${timePreset === p.key ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid={`time-filter-${p.key}`}>{p.label}</button>
              ))}
            </div>
            {timePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="custom-date-from" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="custom-date-to" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${showFilters ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="toggle-filters-btn">
              <Filter size={14} /> Filters
            </button>
            <button onClick={loadData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="gtm-refresh-btn">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 space-y-4" data-testid="expanded-filters">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* User filter */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Filter by User</label>
                <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="user-filter-select">
                  <option value="">All Users</option>
                  {usersList.map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email} ({u.role})</option>)}
                </select>
              </div>
              {/* Compare preset */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Compare With</label>
                <select value={comparePreset} onChange={e => setComparePreset(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="compare-preset-select">
                  {COMPARE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              {comparePreset === 'custom' && (
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Compare Date Range</label>
                  <div className="flex gap-2">
                    <input type="date" value={compCustomFrom} onChange={e => setCompCustomFrom(e.target.value)}
                      className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-xs" data-testid="comp-date-from" />
                    <input type="date" value={compCustomTo} onChange={e => setCompCustomTo(e.target.value)}
                      className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-xs" data-testid="comp-date-to" />
                  </div>
                </div>
              )}
            </div>
            {(userFilter || comparePreset !== 'none' || timePreset !== 'all') && (
              <button onClick={() => { setUserFilter(''); setComparePreset('none'); setCompCustomFrom(''); setCompCustomTo(''); setTimePreset('all'); setCustomFrom(''); setCustomTo(''); clearPageFilters(user?.id, 'gtm_dashboard'); }}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1" data-testid="clear-filters-btn">
                <X size={12} /> Reset Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gtm-kpi-grid">
        {/* Closed Won */}
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="kpi-closed-won">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-500" />
            </div>
            {deltas && <DeltaBadge delta={deltas.closed_won} />}
          </div>
          <p className="text-2xl font-bold text-gray-900">{primary.closed_won.count}</p>
          <p className="text-xs text-gray-500 mt-1">Closed Won Leads</p>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${primary.closed_won.pct}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{primary.closed_won.pct}% of {primary.closed_won.total_leads} leads</p>
        </div>

        {/* Checklist Completion */}
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="kpi-checklist">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <BarChart3 size={20} className="text-blue-500" />
            </div>
            {deltas && <DeltaBadge delta={deltas.checklist_completed} />}
          </div>
          <p className="text-2xl font-bold text-gray-900">{primary.checklist_completion.completed}</p>
          <p className="text-xs text-gray-500 mt-1">Checklist Completed</p>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${primary.checklist_completion.pct}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{primary.checklist_completion.pct}% of {primary.checklist_completion.total} leads</p>
        </div>

        {/* OTA Overall */}
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="kpi-ota-overall">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <Globe2 size={20} className="text-teal-500" />
            </div>
            {deltas && <DeltaBadge delta={deltas.ota_completion_pct} />}
          </div>
          <p className="text-2xl font-bold text-gray-900">{primary.ota_status.overall_pct}%</p>
          <p className="text-xs text-gray-500 mt-1">OTA Completion</p>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${primary.ota_status.overall_pct}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{primary.ota_status.completed} of {primary.ota_status.total} OTA entries</p>
        </div>

        {/* Offboarding */}
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="kpi-offboarding">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <TrendingDown size={20} className="text-amber-500" />
            </div>
            {deltas && <DeltaBadge delta={deltas.offboarding} />}
          </div>
          <p className="text-2xl font-bold text-gray-900">{primary.offboarding.count}</p>
          <p className="text-xs text-gray-500 mt-1">Offboarded</p>
        </div>
      </div>

      {/* Comparison Cards (when active) */}
      {comparison && deltas && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="comparison-section">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 size={16} className="text-teal-600" /> Period Comparison
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Closed Won', curr: deltas.closed_won.current, prev: deltas.closed_won.previous, delta: deltas.closed_won },
              { label: 'Checklist Done', curr: deltas.checklist_completed.current, prev: deltas.checklist_completed.previous, delta: deltas.checklist_completed },
              { label: 'OTA Completion %', curr: `${primary.ota_status.overall_pct}%`, prev: `${comparison.ota_status.overall_pct}%`, delta: deltas.ota_completion_pct },
              { label: 'Offboarded', curr: deltas.offboarding.current, prev: deltas.offboarding.previous, delta: deltas.offboarding },
            ].map(item => (
              <div key={item.label} className="bg-gray-50 rounded-lg p-4" data-testid={`compare-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-xs text-gray-500 mb-2">{item.label}</p>
                <div className="flex items-end gap-3">
                  <div>
                    <p className="text-lg font-bold text-gray-900">{item.curr}</p>
                    <p className="text-[10px] text-gray-400">Current</p>
                  </div>
                  <span className="text-gray-300 text-lg">vs</span>
                  <div>
                    <p className="text-lg font-bold text-gray-400">{item.prev}</p>
                    <p className="text-[10px] text-gray-400">Previous</p>
                  </div>
                </div>
                <div className="mt-2"><DeltaBadge delta={item.delta} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OTA Platform Completion */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="ota-platform-breakdown">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <Globe2 size={16} className="text-teal-600" /> OTA Platform Completion
            {primary.closed_won.count > 0 && <span className="text-xs font-normal text-gray-400 ml-1">(vs {primary.closed_won.count} Closed Won Leads)</span>}
          </h3>
          <button onClick={async () => {
            try {
              const params = {};
              if (userFilter) params.user_filter = userFilter;
              if (timePreset === 'custom') {
                if (customFrom) params.date_from = customFrom;
                if (customTo) params.date_to = customTo;
              } else if (timePreset !== 'all') {
                const range = getDateRange(timePreset);
                if (range.date_from) params.date_from = range.date_from;
                if (range.date_to) params.date_to = range.date_to;
              }
              const res = await gtmDashboardAPI.exportOTA(params);
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              link.download = `ota_data_${new Date().toISOString().split('T')[0]}.csv`;
              link.click();
              window.URL.revokeObjectURL(url);
            } catch (err) { console.error('Export error:', err); }
          }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="gtm-export-ota">
            <Download size={13} /> Export OTA Data
          </button>
        </div>
        {primary.ota_status.platforms.length > 0 ? (
          <div className="space-y-3">
            {primary.ota_status.platforms.map(p => (
              <div key={p.name} className="flex items-center gap-3" data-testid={`ota-platform-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="text-sm font-medium text-gray-700 w-28 shrink-0 truncate">{p.name}</span>
                <div className="flex-1">
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${p.pct === 100 ? 'bg-emerald-500' : p.pct > 60 ? 'bg-teal-500' : p.pct > 30 ? 'bg-amber-500' : 'bg-red-400'}`}
                      style={{ width: `${Math.min(100, p.pct)}%` }} />
                  </div>
                </div>
                <span className="text-xs font-bold text-gray-600 w-14 text-right">{p.pct}%</span>
                <span className="text-xs text-gray-500 w-24 text-right">{p.completed} / {primary.closed_won.count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-6">No OTA data available for selected period</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Weekly Trends */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="weekly-trends-section">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-teal-600" /> {primary.weekly_trends.length > 0 && primary.weekly_trends[0].week && primary.weekly_trends[0].week.match(/^\d/) ? 'Daily Trends' : 'Weekly Trends'}
          </h3>
          {primary.weekly_trends.some(w => w.closed_won > 0 || w.checklist_completed > 0 || w.ota_completed > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={primary.weekly_trends} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="gClosed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gChecklist" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOTA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0d9488" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="closed_won" stroke="#10b981" fill="url(#gClosed)" name="Closed Won" strokeWidth={2} />
                <Area type="monotone" dataKey="checklist_completed" stroke="#3b82f6" fill="url(#gChecklist)" name="Checklist Done" strokeWidth={2} />
                <Area type="monotone" dataKey="ota_completed" stroke="#0d9488" fill="url(#gOTA)" name="OTA Completed" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">No trend data for selected period</p>
          )}
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="top-performers-section">
          <div className="px-5 pt-4 pb-2">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Award size={16} className="text-teal-600" /> Top Performers
            </h3>
          </div>
          {/* Unified Header: Info | Search | Pagination */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
                <span>Showing {Math.max(0, Math.min(perfPageSize, filteredPerformers.length - (perfPage - 1) * perfPageSize))} of {filteredPerformers.length}</span>
                <span className="text-gray-300">|</span>
                <span>Page {perfPage}/{Math.max(1, Math.ceil(filteredPerformers.length / perfPageSize))}</span>
              </div>
              <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={perfSearch} onChange={e => { setPerfSearch(e.target.value); setPerfPage(1); }}
                    placeholder="Search performers..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="perf-search" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="hidden sm:inline">Per page:</span>
                  <select value={perfPageSize} onChange={e => { setPerfPageSize(Number(e.target.value)); setPerfPage(1); }}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="perf-page-size">
                    {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setPerfPage(p => Math.max(1, p - 1))} disabled={perfPage <= 1}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="perf-prev">Prev</button>
                  <button onClick={() => setPerfPage(p => Math.min(Math.ceil(filteredPerformers.length / perfPageSize), p + 1))} disabled={perfPage >= Math.ceil(filteredPerformers.length / perfPageSize)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="perf-next">Next</button>
                </div>
              </div>
            </div>
            <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
              <span>{filteredPerformers.length} performers</span>
              <span>Page {perfPage}/{Math.max(1, Math.ceil(filteredPerformers.length / perfPageSize))}</span>
            </div>
          </div>
          {filteredPerformers.length > 0 ? (
            <div className="p-5 space-y-2.5">
              {filteredPerformers.slice((perfPage - 1) * perfPageSize, perfPage * perfPageSize).map((p, i) => {
                const globalIdx = (perfPage - 1) * perfPageSize + i;
                return (
                  <div key={p.user} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`performer-${globalIdx}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      globalIdx === 0 ? 'bg-amber-100 text-amber-700' : globalIdx === 1 ? 'bg-gray-200 text-gray-700' : globalIdx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                    }`}>#{globalIdx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{p.user}</p>
                      <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-0.5">
                        <span>{p.leads_closed} won</span>
                        <span>{p.total_leads} total</span>
                        <span>{p.tasks_completed} tasks done</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-teal-600">{p.total_leads > 0 ? Math.round(p.leads_closed / p.total_leads * 100) : 0}%</p>
                      <p className="text-[10px] text-gray-400">win rate</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">{perfSearch ? 'No matching performers' : 'No performer data yet'}</p>
          )}
        </div>
      </div>

      {/* At Risk Listings */}
      {primary.at_risk.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="at-risk-section">
          <div className="px-5 pt-4 pb-2">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" /> At Risk Listings
            </h3>
          </div>
          {/* Unified Header: Info | Search | Pagination */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
                <span>Showing {Math.max(0, Math.min(riskPageSize, filteredAtRisk.length - (riskPage - 1) * riskPageSize))} of {filteredAtRisk.length}</span>
                <span className="text-gray-300">|</span>
                <span>Page {riskPage}/{Math.max(1, Math.ceil(filteredAtRisk.length / riskPageSize))}</span>
              </div>
              <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={riskSearch} onChange={e => { setRiskSearch(e.target.value); setRiskPage(1); }}
                    placeholder="Search at risk..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="risk-search" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="hidden sm:inline">Per page:</span>
                  <select value={riskPageSize} onChange={e => { setRiskPageSize(Number(e.target.value)); setRiskPage(1); }}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="risk-page-size">
                    {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setRiskPage(p => Math.max(1, p - 1))} disabled={riskPage <= 1}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="risk-prev">Prev</button>
                  <button onClick={() => setRiskPage(p => Math.min(Math.ceil(filteredAtRisk.length / riskPageSize), p + 1))} disabled={riskPage >= Math.ceil(filteredAtRisk.length / riskPageSize)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="risk-next">Next</button>
                </div>
              </div>
            </div>
            <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
              <span>{filteredAtRisk.length} items</span>
              <span>Page {riskPage}/{Math.max(1, Math.ceil(filteredAtRisk.length / riskPageSize))}</span>
            </div>
          </div>
          {filteredAtRisk.length > 0 ? (
            <div className="p-5 space-y-2">
              {filteredAtRisk.slice((riskPage - 1) * riskPageSize, riskPage * riskPageSize).map((r, i) => {
                const globalIdx = (riskPage - 1) * riskPageSize + i;
                return (
                  <div key={`${r.lead_id}-${globalIdx}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`risk-item-${globalIdx}`}>
                    <AlertCircle size={16} className={r.type === 'incomplete_ota' ? 'text-amber-500 mt-0.5' : 'text-red-400 mt-0.5'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.type === 'incomplete_ota' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          {r.type === 'incomplete_ota' ? 'Incomplete OTA' : 'Low Checklist'}
                        </span>
                        {r.owner && <span className="text-[10px] text-gray-400">{r.owner}</span>}
                      </div>
                      <p className="text-sm font-medium text-gray-800 truncate">{r.hotel_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{r.detail}</p>
                      {r.type === 'incomplete_ota' && (
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                          {r.completed_otas && r.completed_otas.length > 0 && (
                            <div className="flex items-center gap-1 text-[10px]">
                              <CheckCircle size={10} className="text-emerald-500 shrink-0" />
                              <span className="text-emerald-700 font-medium">Done:</span>
                              <span className="text-gray-600">{r.completed_otas.join(', ')}</span>
                            </div>
                          )}
                          {r.pending_otas && r.pending_otas.length > 0 && (
                            <div className="flex items-center gap-1 text-[10px]">
                              <AlertTriangle size={10} className="text-amber-500 shrink-0" />
                              <span className="text-amber-700 font-medium">Pending:</span>
                              <span className="text-gray-600">{r.pending_otas.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">{riskSearch ? 'No matching at risk items' : 'No at risk items'}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default GTMDashboard;
